import React from 'react'

import classNames from 'classnames'

import { PaymentType } from 'src/apollo/onlineOrdering'
import { t } from 'src/translations'

import ItemPrice from 'public/components/default_template/online_ordering/order/ItemPrice'
import { Surcharges } from 'public/components/online_ordering/CartContext'
import { isToastLocalRequest } from 'src/public/js/siteUtilities'
import { useStaticSiteContext } from 'src/public/components/app/StaticSiteContext'

export type SurchargingConfig = {
  enabled?: boolean | null
  percent?: number | null
}

export type CreditCardSurchargeDisclaimerProps = {
  surcharges?: Surcharges | null
  spiSurchargingEnabled?: boolean
  className?: string | null
  restaurantName?: string
  surchargingConfig?: SurchargingConfig | null
}

type CreditCardSurchargeDisclaimerWithItemPriceProps = {
  loadingCart: boolean
}

type CreditCardSurchargeDisclaimerBaseProps = {
  surchargeAmount: number
  surchargeRatePercent?: number | null
  className?: string | null
  restaurantName?: string
  enabled?: boolean | null
}

export const CreditCardSurchargeDisclaimerBase = ({
  surchargeAmount,
  surchargeRatePercent,
  className,
  restaurantName,
  enabled
}: CreditCardSurchargeDisclaimerBaseProps) => {
  // Only show if surcharging is enabled
  if (!enabled) {
    return null
  }

  const surchargeRateForDisplay =
    surchargeRatePercent && surchargeRatePercent >= 0
      ? `${surchargeRatePercent.toFixed(2)}%`
      : ''
  const establishmentName = restaurantName || t('common.this-establishment')

  return (
    <div
      className={classNames('creditCardSurchargeDisclaimerBase', className)}
      data-testid='surcharge-disclaimer'
      role='complementary'
      aria-label={t('accessibility.credit-card-surcharge-notice')}
    >
      {t('surcharge-disclaimer.full-disclaimer', {
        establishmentName,
        surchargeRate: surchargeRateForDisplay,
        surchargeAmount: `$${(surchargeAmount || 0).toFixed(2)}`
      })}
    </div>
  )
}

export const CreditCardSurchargeDisclaimer = ({
  surcharges,
  className,
  restaurantName,
  surchargingConfig
}: CreditCardSurchargeDisclaimerProps) => {
  const creditSurcharge = surcharges?.[PaymentType.Credit]
  const staticContext = useStaticSiteContext()
  const isToastLocal = isToastLocalRequest(staticContext)
  //displays the establishment name on toast local
  const establishmentName =
    isToastLocal && restaurantName
      ? restaurantName
      : t('common.this-establishment')

  // Prefer cart values if available, fall back to config
  const surchargeAmount = creditSurcharge?.surchargeAmount || 0
  const surchargeRatePercent =
    creditSurcharge?.surchargeRate != null
      ? creditSurcharge.surchargeRate * 100
      : surchargingConfig?.percent ?? null
  const enabled = surchargingConfig?.enabled === true

  // Only render if enabled
  if (!enabled) {
    return null
  }

  return (
    <CreditCardSurchargeDisclaimerBase
      surchargeAmount={surchargeAmount}
      surchargeRatePercent={surchargeRatePercent}
      className={className}
      restaurantName={establishmentName}
      enabled={enabled}
    />
  )
}

/**
 * For use in the OrderPrices component on the CheckoutPage
 */
export const CreditCardSurchargeDisclaimerWithItemPrice = ({
  surcharges,
  spiSurchargingEnabled,
  loadingCart
}: CreditCardSurchargeDisclaimerProps &
  CreditCardSurchargeDisclaimerWithItemPriceProps) => {
  const creditSurcharge = surcharges?.[PaymentType.Credit]

  // Don't show if surcharging is not enabled for this restaurant
  if (!spiSurchargingEnabled) {
    return null
  }

  // Only show if there are actual surcharges configured (items in cart)
  if (
    !creditSurcharge ||
    !creditSurcharge.surchargeAmount ||
    !creditSurcharge.surchargeRate
  ) {
    return null
  }

  return (
    <div className='row surcharge'>
      <ItemPrice
        loading={loadingCart}
        title={t('surcharge-disclaimer.credit-card-surcharge', {
          percentage: creditSurcharge.surchargeRate * 100
        })}
        amount={creditSurcharge.surchargeAmount}
        amountPrefix='+'
      />
      {/* eslint-disable-next-line react/jsx-closing-tag-location */}
    </div>
  )
}
